import { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { RiCloseLine, RiCheckLine, RiMessage3Fill } from '@remixicon/react';
import { toast } from 'sonner';
import PhoneInput from '@components/forms/PhoneInput';
import { COUNTRY_CODES } from '@/utils/countryCode';
import { maskPhone } from '../utils/format';
import VerifyCodeStep from './VerifyCodeStep';

// Flip to true to require SMS verification before saving.
const VERIFY_PHONE_ENABLED = false;

// Split a stored E.164-ish phone ("+19999999999") into { code, digits }.
const splitPhone = (raw, fallbackCode = '+1') => {
  const value = (raw || '').trim();
  if (!value) return { code: fallbackCode, digits: '' };
  if (value.startsWith('+')) {
    const sorted = [...COUNTRY_CODES].sort((a, b) => b.code.length - a.code.length);
    const match = sorted.find((c) => value.startsWith(c.code));
    if (match) return { code: match.code, digits: value.slice(match.code.length).replace(/\D/g, '') };
  }
  return { code: fallbackCode, digits: value.replace(/\D/g, '') };
};

const UpdatePhoneModal = ({ open, onClose, onSubmit, initialCountryCode = '+1', initialPhone = '' }) => {
  const initial = splitPhone(initialPhone, initialCountryCode);
  const [step, setStep] = useState('enter');
  const [countryCode, setCountryCode] = useState(initial.code);
  const [phone, setPhone] = useState(initial.digits);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      const next = splitPhone(initialPhone, initialCountryCode);
      setStep('enter');
      setCountryCode(next.code);
      setPhone(next.digits);
      setSaving(false);
    }
  }, [open, initialCountryCode, initialPhone]);

  if (!open) return null;

  const canSubmit = phone.replace(/\D/g, '').length >= 7 && !saving;

  const sendVerificationCode = () => {
    // TODO: wire to verification endpoint
    const code = String(Math.floor(100000 + Math.random() * 900000));
    console.log('Send verification code', code, 'to', countryCode, phone);
    toast.custom(
      (id) => (
        <div
          onClick={() => toast.dismiss(id)}
          className="flex items-start gap-3 w-[360px] max-w-[90vw] px-3 py-3 rounded-2xl bg-white/60 backdrop-blur-xl shadow-lg border border-white/40 cursor-pointer"
        >
          <div className="w-9 h-9 rounded-lg bg-green-500 flex items-center justify-center flex-shrink-0">
            <RiMessage3Fill className="w-5 h-5 text-white" />
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-[13px] font-semibold text-gray-900 leading-tight">TrustJet</p>
            <p className="text-[13px] text-gray-700 leading-snug mt-0.5">
              Your verification code is <span className="font-medium">{code}</span>. Do not share this code to anyone. If you did not request this code, please ignore this message.
            </p>
          </div>
        </div>
      ),
      { duration: 3000 }
    );
  };

  const savePhone = async () => {
    setSaving(true);
    try {
      await onSubmit?.({ countryCode, phone });
      onClose();
      toast.success('Phone updated successfully', { position: 'bottom-right' });
    } catch (err) {
      toast.error(err?.message || 'Failed to update phone', { position: 'bottom-right' });
    } finally {
      setSaving(false);
    }
  };

  const handlePrimaryAction = () => {
    if (!canSubmit) return;
    if (VERIFY_PHONE_ENABLED) {
      sendVerificationCode();
      setStep('verify');
    } else {
      savePhone();
    }
  };

  const handleResend = () => {
    sendVerificationCode();
  };

  const handleVerified = async (code) => {
    // TODO: wire to verify endpoint
    console.log('Verify code', code, 'for', countryCode, phone);
    await savePhone();
  };

  return createPortal(
    <>
      <div className="fixed inset-0 z-[9998] bg-black/30" onClick={onClose} />
      <div className="fixed z-[9999] top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[calc(100%-2rem)] max-w-md bg-white rounded-3xl shadow-xl overflow-hidden">
        {step === 'enter' ? (
          <>
            <div className="flex items-center justify-between px-6 pt-5 pb-1">
              <h3 className="text-lg font-semibold text-primary-700">Update phone number</h3>
              <button
                type="button"
                onClick={onClose}
                className="p-1 text-gray-400 hover:text-gray-600 transition-colors"
                aria-label="Close"
              >
                <RiCloseLine className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 pt-3 pb-6">
              <PhoneInput
                countryCode={countryCode}
                phone={phone}
                onCountryCodeChange={setCountryCode}
                onPhoneChange={setPhone}
                label="New Phone Number"
                required
              />
              <button
                type="button"
                onClick={handlePrimaryAction}
                disabled={!canSubmit}
                className={`mt-5 w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-medium transition-colors ${
                  canSubmit
                    ? 'bg-primary-600 text-white hover:bg-primary-700'
                    : 'bg-gray-100 text-gray-400 cursor-not-allowed'
                }`}
              >
                <RiCheckLine className="w-4 h-4" />
                {saving ? 'Saving...' : 'Save phone number'}
              </button>
            </div>
          </>
        ) : (
          <VerifyCodeStep
            maskedPhone={maskPhone(phone.replace(/\D/g, ''))}
            onResend={handleResend}
            onVerified={handleVerified}
            onClose={onClose}
          />
        )}
      </div>
    </>,
    document.body
  );
};

export default UpdatePhoneModal;
